<template>
  <Modal
    ref="modal"
    class="UpdateGroupModal"
    :heading="messages.group.updateGroupHeading"
    :size="isExtraSmallPixel ? 'extraSmall' : 'small'"
    @closeModal="onClickModalClose"
  >
    <template v-if="!isError">
      <ul>
        <li class="UpdateGroupModal__item">
          <label class="UpdateGroupModal__label">{{
            messages.group.groupName
          }}</label>
          <TextField
            id="name"
            class="UpdateGroupModal__textField"
            type="text"
            name="name"
            :ariaLabel="messages.group.groupNameAriaLabel"
            :placeholder="messages.group.groupName"
            :isError="isEmptyError || isLengthError"
            :errorText="
              isEmptyError
                ? commonMessage.requiredField(messages.group.groupName)
                : isLengthError
                ? commonMessage.lengthValidationError(
                    messages.group.groupName,
                    50
                  )
                : ''
            "
            :value="name"
            @input="name = $event.target.value"
          />
        </li>
      </ul>
      <div class="UpdateGroupModal__modalButton">
        <Button
          tag="button"
          dynamics="secondary"
          :label="messages.common.cancel"
          @click="onClickModalClose"
        />
        <Button
          tag="button"
          dynamics="primary"
          :label="messages.common.change"
          :isDisabled="isInvalidName || isLengthError"
          @click="onClickUpdate"
        />
      </div>
    </template>
    <div v-else class="UpdateGroupModal__modalInner">
      <Icon class="UpdateGroupModal__icon" iconName="Exception"></Icon>
      <!-- safe usage of v-html: content is fully static and developer-controlled -->
      <p
        class="UpdateGroupModal__modalText"
        v-html="messages.group.fail.updateGroup"
      ></p>
      <div class="UpdateGroupModal__modalButton">
        <Button
          tag="button"
          dynamics="secondary"
          :label="messages.common.close"
          @click="onClickModalClose"
        />
      </div>
    </div>
  </Modal>
</template>

<script lang="ts">
export default {
  name: 'UpdateGroupModal',
};
</script>

<script lang="ts" setup>
import TextField from '@spectee/components/Atoms/TextField/index.vue';
import Button from '@spectee/components/Atoms/Button/index.vue';
import Modal from '@spectee/components/Organisms/Modal/index.vue';
import Icon from '@spectee/components/Atoms/IconBase/index.vue';
import { commonMessage } from '@spectee/const/messageContents';
import { lengthValidate } from '@spectee/ts/validation';
import { ref, watchEffect } from 'vue';
import { useWindowWidth } from '@spectee/composables/useWindowWidth';
import messages from '../../../../messages.json';

type Props = {
  groupName: string;
  isError: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  groupName: '',
  isError: false,
});

const emit = defineEmits<{
  (e: 'onClickModalClose'): void;
  (e: 'updateGroup', groupName: string): void;
}>();

const onClickModalClose = () => {
  emit('onClickModalClose');
  return;
};

const { isExtraSmallPixel } = useWindowWidth();

const name = ref(props.groupName);

const isInvalidName = ref(false);
const isLengthError = ref(false);
const isEmptyError = ref(false);

watchEffect(() => {
  isInvalidName.value = !name.value || props.groupName === name.value;
  isLengthError.value = name.value ? !lengthValidate(name.value, 50) : false;
  isEmptyError.value = !name.value?.trim();
});

const onClickUpdate = async () => {
  emit('updateGroup', name.value);
};
</script>

<style lang="scss" scoped>
@use '@spectee/assets/scss/_forward.scss' as *;
.UpdateGroupModal {
  padding: $service-unit * 4 $service-unit * 4 $service-unit * 6;

  &__item {
    margin-top: $service-unit * 6;
    font-size: $service-font-size-p;
  }

  &__label {
    font-weight: bold;
  }

  &__modalButton {
    display: flex;
    margin-top: $service-unit * 6;
    gap: $service-unit * 3;
    justify-content: center;
  }

  &__modalInner {
    text-align: center;
  }

  &__modalText {
    margin-top: $service-unit * 6;
    font-size: $service-font-size-p;
    line-height: $service-line-height-mf;
  }

  &__icon {
    width: $service-unit * 10;
    margin: $service-unit * 4 $service-unit * 38 0 $service-unit * 38;
    fill: #c33b2e;
  }

  @media (max-width: 424px) {
    &__icon {
      margin-left: $service-unit * 28;
    }
  }
}
</style>
